import {
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Pagination
} from "@nextui-org/react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import utils from "../../auth/data/utils";
import { LoadingState } from "@react-types/shared";
import { currencyFormat, numFormat } from "../../auth/data/tools";
import { useAuth } from "../../context/AuthContext";
import React from "react";
import moment from "moment";

export enum ColumnTypes {
    Text        = 0,
    Amount      = 1,
    Currency    = 2,
    Date        = 3,
    Component   = 4,
    Number      = 5
}

export interface TblColumn<T = any> {
    dataIndex?: string|null;
    componentRender?: (componentProps: { item: T }) => React.ReactElement;
    columnType: ColumnTypes;
    columnName: string;
    allowsSorting?: boolean;
}

interface DisplayTableProps {
    tableColumns: TblColumn[];
    tableData: object[];
    noContent?: string;
    totalResults: number;
    loading?: boolean;
    currentPage: number;
    onPaginate: (page: number) => void;
}

const DisplayTable:React.FC<DisplayTableProps> = ({tableData, tableColumns, onPaginate, noContent = "No hay información para mostrar", loading = false, totalResults = 0, currentPage = 1}) => {
    const { userInfo } = useAuth();
    const [totalData, setTotalData] = useState(1);
    const [loadingTable, setLoadingTable] = useState<LoadingState>("idle");
    const pagination = utils().pagination;

    useEffect(() => {
        if(loading)
        {
            setLoadingTable("loading");
        }
        else
        {
            setLoadingTable("idle");
        }
    }, [loading]);

    useEffect(() => {
        setTotalData(totalResults);
    }, [totalResults]);

    const pages = useMemo(() => {
        return totalData ? Math.ceil(totalData / pagination) : 0;
    }, [totalData, pagination]);

    const cellAligns:Record<ColumnTypes, "right" | "left" | "center" | "justify"> = {
        [ColumnTypes.Amount]: "right",
        [ColumnTypes.Currency]: "right",
        [ColumnTypes.Text]: "left",
        [ColumnTypes.Date]: "left",
        [ColumnTypes.Component]: "center",
        [ColumnTypes.Number]: "right"
    }

    return(
        <Table
            isHeaderSticky
            bottomContent={
                <div className="flex w-full justify-center items-center">
                    {
                        pages > 0 ? (
                                <Pagination
                                    isCompact
                                    showControls
                                    showShadow
                                    color="primary"
                                    page={currentPage}
                                    total={pages}
                                    onChange={onPaginate}
                                />
                        ) : null
                    }
                </div>
            }
        >
            <TableHeader>
            {
                tableColumns.map((column: TblColumn, key) => 
                (
                    <TableColumn key={key} className="text-center" allowsSorting={column.allowsSorting}>
                        {column.columnName}
                    </TableColumn>
                )
            )}
            </TableHeader>
            <TableBody
                emptyContent={noContent}
                loadingContent={<Spinner/>}
                loadingState={loadingTable}
            >
                {tableData.map((dataRow, rowKey) => (
                    <TableRow
                        key={rowKey}
                        className="hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    >
                        {tableColumns.map((column, colKey) => (
                            <TableCell 
                                key={colKey}
                                style={{
                                    "textAlign": cellAligns[column.columnType] ?? "left"
                                }}
                            >
                                {renderCell(dataRow, column, userInfo.decimals)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

const renderCell = (dataRow: { [key: string]: any }, column: TblColumn, dec: number):ReactNode => {
    switch (column.columnType)
    {
        case ColumnTypes.Text:
            if(column.dataIndex)
                return <>{dataRow[column.dataIndex]}</>

            return <></>

        case ColumnTypes.Date:
            if(column.dataIndex)
                return <>{moment(dataRow[column.dataIndex]).format('YYYY-MM-DD')}</>

            return <></>
        
        case ColumnTypes.Currency:
            if(column.dataIndex)
                return <>{currencyFormat(dataRow[column.dataIndex], dec)}</>

            return <>{currencyFormat(0, dec)}</>
        
        case ColumnTypes.Amount:
            if(column.dataIndex)
                return <>{numFormat(dataRow[column.dataIndex], dec)}</>

            return <>{numFormat(0, dec)}</>

        case ColumnTypes.Number:
            if(column.dataIndex)
                return <>{numFormat(dataRow[column.dataIndex], 0)}</>

            return <>{numFormat(0, dec)}</>

        case ColumnTypes.Component:
            if(column.componentRender)
                return <column.componentRender item={dataRow}/>;

            return <></>

        default:
            return <></>;
    }
}

export default DisplayTable;